import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

// Kind 34609 = Magazine (Publication)
// Kind 39731 = Magazine Issue
// Kind 33953 = Ebook

export interface MagazineEvent extends NostrEvent {
  kind: 34609;
}

export interface MagazineIssueEvent extends NostrEvent {
  kind: 39731;
}

export interface EbookEvent extends NostrEvent {
  kind: 33953;
}

/** All magazine-related events for a pubkey */
export interface MagazinesData {
  magazines: NostrEvent[];
  issues: NostrEvent[];
  ebooks: NostrEvent[];
}

export function useMagazines(pubkey: string | undefined, enabled = true) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['magazines', pubkey],
    queryFn: async (c): Promise<MagazinesData> => {
      if (!pubkey) return { magazines: [], issues: [], ebooks: [] };

      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(5000)]);

      const events = await nostr.query(
        [{ kinds: [34609, 39731, 33953], authors: [pubkey], limit: 100 }],
        { signal }
      );

      return {
        magazines: events
          .filter((e) => e.kind === 34609 && validateMagazine(e))
          .sort((a, b) => b.created_at - a.created_at),
        issues: events
          .filter((e) => e.kind === 39731 && validateIssue(e))
          .sort((a, b) => b.created_at - a.created_at),
        ebooks: events
          .filter((e) => e.kind === 33953 && validateEbook(e))
          .sort((a, b) => b.created_at - a.created_at),
      };
    },
    enabled: !!pubkey && enabled,
    staleTime: 0,
  });
}

/** Single magazine + its issues */
export function useMagazineDetail(pubkey: string | undefined, dTag: string | undefined) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['magazine-detail', pubkey, dTag],
    queryFn: async (c) => {
      if (!pubkey || !dTag) return null;

      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(5000)]);

      // Fetch the magazine event
      const [magazines, issues] = await Promise.all([
        nostr.query(
          [{ kinds: [34609], authors: [pubkey], '#d': [dTag], limit: 1 }],
          { signal }
        ),
        nostr.query(
          [{ kinds: [39731], '#a': [`34609:${pubkey}:${dTag}`], limit: 50 }],
          { signal }
        ),
      ]);

      const magazine = magazines[0];
      if (!magazine || !validateMagazine(magazine)) return null;

      return {
        magazine,
        issues: issues.filter(validateIssue).sort((a, b) => b.created_at - a.created_at),
      };
    },
    enabled: !!pubkey && !!dTag,
    staleTime: 0,
  });
}

/** Single ebook by d-tag */
export function useEbook(pubkey: string | undefined, dTag: string | undefined) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['ebook', pubkey, dTag],
    queryFn: async (c) => {
      if (!pubkey || !dTag) return null;
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(5000)]);
      const events = await nostr.query(
        [{ kinds: [33953], authors: [pubkey], '#d': [dTag], limit: 1 }],
        { signal }
      );
      const e = events[0];
      return e && validateEbook(e) ? e : null;
    },
    enabled: !!pubkey && !!dTag,
  });
}

// ── Validators ────────────────────────────────────────────────────

export function validateMagazine(e: NostrEvent): boolean {
  if (e.kind !== 34609) return false;
  return e.tags.some(([n]) => n === 'd') && e.tags.some(([n]) => n === 'title');
}

export function validateIssue(e: NostrEvent): boolean {
  if (e.kind !== 39731) return false;
  return (
    e.tags.some(([n]) => n === 'd') &&
    e.tags.some(([n]) => n === 'title') &&
    e.tags.some(([n]) => n === 'a')
  );
}

export function validateEbook(e: NostrEvent): boolean {
  if (e.kind !== 33953) return false;
  return (
    e.tags.some(([n]) => n === 'd') &&
    e.tags.some(([n]) => n === 'title') &&
    e.tags.some(([n]) => n === 'url')
  );
}

// ── Tag helpers ───────────────────────────────────────────────────

export const getTag = (e: NostrEvent, name: string) =>
  e.tags.find(([n]) => n === name)?.[1] ?? null;

export const getTags = (e: NostrEvent, name: string) =>
  e.tags.filter(([n]) => n === name).map(([, v]) => v).filter(Boolean) as string[];

/** Return the magazine's d-tag from an issue's `a` tag, e.g. "34609:pubkey:slug" → "slug" */
export function magazineDTagFromIssue(e: NostrEvent): string | null {
  const a = getTag(e, 'a');
  if (!a) return null;
  const parts = a.split(':');
  return parts[2] ?? null;
}

/** Format file size for display */
export function formatFileSize(bytes: string | null): string | null {
  if (!bytes) return null;
  const n = parseInt(bytes);
  if (isNaN(n)) return null;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
